import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  User, Mail, Phone, MapPin, Calendar, CreditCard, 
  FileText, TrendingUp, Shield, ArrowLeft, Edit, Save,
  Trash2, Download, Send, Home
} from "lucide-react";
import MobileNav from "../components/MobileNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchClient();
    fetchAccounts();
  }, [id]);

  const fetchClient = async () => {
    try {
      const res = await axios.get(`${API}/clients/${id}`);
      setClient(res.data);
      setFormData(res.data);
    } catch (err) {
      toast.error("Failed to load client");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API}/accounts?client_id=${id}`);
      setAccounts(res.data || []);
    } catch (err) {
      console.error("Failed to load accounts:", err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/clients/${id}`, formData);
      setClient(formData);
      setEditing(false);
      toast.success("Client updated successfully");
    } catch (err) {
      toast.error("Failed to update client");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success("Client deleted");
      navigate("/clients");
    } catch (err) {
      toast.error("Failed to delete client");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-400">Client not found</p>
        <Button onClick={() => navigate("/clients")} className="mt-4">
          Back to Clients
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <MobileNav />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/clients")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{client.name}</h1>
            <p className="text-zinc-400">{client.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate(`/dispute-wizard/${id}`)}
            className="border-orange-500 text-orange-400 hover:bg-orange-500/10"
          >
            <Shield className="h-4 w-4 mr-2" />
            Start Dispute
          </Button>
          <Button variant="outline" onClick={() => setEditing(!editing)}>
            <Edit className="h-4 w-4 mr-2" />
            {editing ? "Cancel" : "Edit"}
          </Button>
          {editing && (
            <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
              <Save className="h-4 w-4 mr-2" />
              Save
            </Button>
          )}
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Tabs defaultValue="info" className="space-y-6">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="info">Client Info</TabsTrigger>
          <TabsTrigger value="accounts">Accounts ({accounts.length})</TabsTrigger>
          <TabsTrigger value="scores">Credit Scores</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Full Name</Label>
                    {editing ? (
                      <Input
                        value={formData.name || ""}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    ) : (
                      <p className="text-lg">{client.name}</p>
                    )}
                  </div>
                  <div>
                    <Label>Email</Label>
                    {editing ? (
                      <Input
                        value={formData.email || ""}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    ) : (
                      <p className="text-lg">{client.email}</p>
                    )}
                  </div>
                  <div>
                    <Label>Phone</Label>
                    {editing ? (
                      <Input
                        value={formData.phone || ""}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    ) : (
                      <p className="text-lg">{client.phone || "Not provided"}</p>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label>Address</Label>
                    {editing ? (
                      <Input
                        value={formData.address || ""}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    ) : (
                      <p className="text-lg">{client.address || "Not provided"}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>City</Label>
                      {editing ? (
                        <Input
                          value={formData.city || ""}
                          onChange={(e) => setFormData({...formData, city: e.target.value})}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      ) : (
                        <p>{client.city || "-"}</p>
                      )}
                    </div>
                    <div>
                      <Label>State</Label>
                      {editing ? (
                        <Input
                          value={formData.state || ""}
                          onChange={(e) => setFormData({...formData, state: e.target.value})}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      ) : (
                        <p>{client.state || "-"}</p>
                      )}
                    </div>
                    <div>
                      <Label>ZIP</Label>
                      {editing ? (
                        <Input
                          value={formData.zip_code || ""}
                          onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                          className="bg-zinc-800 border-zinc-700"
                        />
                      ) : (
                        <p>{client.zip_code || "-"}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Date of Birth</Label>
                    {editing ? (
                      <Input
                        type="date"
                        value={formData.date_of_birth || ""}
                        onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                      />
                    ) : (
                      <p className="text-lg">{client.date_of_birth || "Not provided"}</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="accounts">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>Accounts</CardTitle>
            </CardHeader>
            <CardContent>
              {accounts.length === 0 ? (
                <div className="text-center py-8">
                  <CreditCard className="h-12 w-12 mx-auto text-zinc-600 mb-3" />
                  <p className="text-zinc-400">No accounts found</p>
                  <Button 
                    onClick={() => navigate(`/dispute-wizard/${id}`)}
                    className="mt-4 bg-orange-500 hover:bg-orange-600"
                  >
                    Import Credit Report
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {accounts.map((account) => (
                    <div key={account.id} className="p-4 rounded-lg bg-zinc-800 border border-zinc-700">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold">{account.creditor_name}</p>
                          <p className="text-sm text-zinc-400">
                            {account.account_number ? `****${account.account_number.slice(-4)}` : "N/A"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-red-400">
                            ${(account.balance || 0).toLocaleString()}
                          </p>
                          <p className={`text-sm ${
                            account.status === 'removed' ? 'text-green-400' :
                            account.status === 'disputed' ? 'text-yellow-400' :
                            'text-zinc-400'
                          }`}>
                            {account.status || 'Pending'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scores">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>Credit Scores</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-6 rounded-xl bg-red-500/10 border border-red-500/30">
                  <p className="text-sm text-zinc-400 mb-2">Equifax</p>
                  <p className="text-4xl font-bold text-red-400">
                    {client.starting_score_equifax || client.score_equifax || "-"}
                  </p>
                </div>
                <div className="text-center p-6 rounded-xl bg-blue-500/10 border border-blue-500/30">
                  <p className="text-sm text-zinc-400 mb-2">Experian</p>
                  <p className="text-4xl font-bold text-blue-400">
                    {client.starting_score_experian || client.score_experian || "-"}
                  </p>
                </div>
                <div className="text-center p-6 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                  <p className="text-sm text-zinc-400 mb-2">TransUnion</p>
                  <p className="text-4xl font-bold text-yellow-400">
                    {client.starting_score_transunion || client.score_transunion || "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
